import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { CdkTreeModule } from '@angular/cdk/tree';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { fetchAuthSession } from '@aws-amplify/auth';
import { akitaDevtools } from '@datorama/akita';
import { AkitaNgRouterStoreModule } from '@datorama/akita-ng-router-store';
import { NgSelectModule } from '@ng-select/ng-select';
import { Amplify } from 'aws-amplify';
import { NgChartsModule } from 'ng2-charts';
import { TimeagoModule } from 'ngx-timeago';

import { ApiService } from '@shared/services/api.service';
import { AppInitService } from '@shared/services/app-init.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { isDev } from '@shared/utils/utils';

import { appServiceInit, initUser, launchDarklyInit } from './app/app-initializers';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { provideEventPreventStopPlugin } from './app/plugins/event-prevent-stop/event-prevent-stop';
import { provideTanstack } from './app/plugins/tanstack';
import { environment } from './environments/environment';

// static imports
import '@ag-grid-enterprise/core';
import './app/plugins/ag-grid';
import './app/chart-default-styles';

if (isDev) {
  akitaDevtools();
}

const cognitoOAuthOverride =
  environment.cognito.loginWith.oauth && location.hostname === 'localhost'
    ? {
        loginWith: {
          oauth: {
            ...environment.cognito.loginWith.oauth,
            redirectSignIn: [location.origin],
            redirectSignOut: [location.origin],
          },
        },
      }
    : {};

Amplify.configure(
  {
    API: {
      GraphQL: {
        ...environment.API,
      },
    },
    Auth: {
      Cognito: {
        ...environment.cognito,
        ...cognitoOAuthOverride,
      },
    },
    Storage: {
      S3: {
        ...environment.S3,
      },
    },
  },
  {
    API: {
      GraphQL: {
        headers: async () => ({
          Authorization: (await fetchAuthSession()).tokens?.idToken?.toString() as string,
        }),
      },
    },
  }
);

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      TimeagoModule,
      AkitaNgRouterStoreModule,
      FormsModule,
      ReactiveFormsModule,
      OverlayModule,
      PortalModule,
      NgSelectModule,
      NgChartsModule,
      NgxSliderModule,
      CdkTreeModule,
      DragDropModule
    ),
    provideAppInitializer(() => {
      const initializerFn = launchDarklyInit(inject(LaunchDarklyService));

      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appServiceInit(inject(ApiService), inject(AppInitService));

      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initUser(inject(AuthService), inject(Router));

      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideZonelessChangeDetection(),
    provideTanstack(),
    provideEventPreventStopPlugin(),
    provideAnimations(),
  ],
}).catch((err) => console.error(err));
